import moment from "moment";
import React, { useMemo, useState } from "react";
import { ButtonDropdown, ButtonGroup, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";

export const yearMonthFormat = "YYYY-MM";
const FROM_DATE = "2023-05";

interface YearMonthSelectorProps {
  date?: moment.Moment;
  setDate: (date: moment.Moment | ((prev: moment.Moment) => moment.Moment)) => void;
}

const now = moment();
const YearMonthSelector = ({ date = now, setDate }: YearMonthSelectorProps) => {
  const [open, setOpen] = useState<string | null>(null);

  const [year, month, monthText] = useMemo(() => [date.format("YYYY"), date.format("MM"), date.format("MMMM")], [date]);

  // Note: 'now' is a module-level constant, computed once on load
  const yearOptions = useMemo(() => {
    const year = moment(now).endOf("y");
    const years = [];
    do {
      years.push(year.format("YYYY"));
      year.subtract(1, "y");
    } while (year.isSameOrAfter(moment(FROM_DATE, yearMonthFormat)));
    return years;
  }, []);

  const monthOptions = useMemo(() => {
    let month = moment(year, "YYYY").endOf("y");
    if (month.isAfter(now)) month = moment(now);

    let limit = moment(month).startOf("y");
    const fromMom = moment(FROM_DATE, yearMonthFormat);
    if (fromMom.isAfter(limit)) limit = fromMom;

    const months = [];
    do {
      months.push({ value: month.format("MM"), label: month.format("MMMM") });
      month.subtract(1, "M");
    } while (month.isSameOrAfter(limit));
    return months;
  }, [year]);

  return (
    <ButtonGroup>
      <ButtonDropdown isOpen={open === "M"} toggle={() => setOpen(open !== "M" ? "M" : null)}>
        <DropdownToggle size="md" caret color="default" className="mt--2" style={{ minWidth: 100 }}>
          {monthText || "-------"}
        </DropdownToggle>
        <DropdownMenu right>
          {monthOptions?.map(({ value, label }, i) => (
            <DropdownItem
              key={i}
              active={month === value}
              onClick={() => setDate(() => moment(date).set("M", parseInt(value) - 1))}
              className={"text-center font-weight-bold " + (month === value ? "bg-default text-white" : "")}
            >
              {label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </ButtonDropdown>

      <ButtonDropdown isOpen={open === "Y"} toggle={() => setOpen(open !== "Y" ? "Y" : null)}>
        <DropdownToggle size="md" caret className="mt--2" color="default">
          {year || "----"}
        </DropdownToggle>
        <DropdownMenu right>
          {yearOptions?.map((y, i) => (
            <DropdownItem
              key={i}
              active={year === y}
              onClick={() =>
                setDate(() => {
                  // Keeping the month index when changing year could land on a
                  // future month, which is not even offered by the month
                  // dropdown, so the selector showed a month that could not be
                  // re-selected and the query came back empty.
                  const next = moment(date).set("y", parseInt(y, 10));
                  return next.isAfter(moment(now)) ? moment(now) : next;
                })
              }
              className={"text-center font-weight-bold " + (year === y ? "bg-default text-white" : "")}
            >
              {y}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </ButtonDropdown>
    </ButtonGroup>
  );
};

export default YearMonthSelector;
